import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { saveAs } from 'file-saver';
import Swal from 'sweetalert2';
import {
  ApiExternaControlService,
  ClienteApi,
  ClienteApiDetalle,
  ConexionClienteApi,
  DocumentacionControlApi,
  EstadoClienteApi,
  FilaUsoClienteApi,
  InterruptorApi,
  LlaveClienteApi,
  ProductoApi,
  ResumenClientesApi,
  UsuarioCandidatoApi,
} from 'src/app/shared/services/api-externa-control.service';
import { ArchivoDescargado } from 'src/app/shared/utils/descarga-archivo';

const TAMANOS_PAGINA = [20, 50, 100];
const POR_PAGINA_DEFAULT = 20;
/** Refresco automatico de lista y resumen, solo con la pestana visible. */
const REFRESCO_MS = 60_000;
const LIMITE_DEFAULT = 30;
const MAX_LLAVES_DEFAULT = 1;
const VERDE = '#2F4A2B';
const ROJO = '#A3432B';
const NOMBRE_OPENAPI = 'aguacate-integraciones-v1.openapi.yaml';

/** Tarjeta del resumen; las tres ultimas filtran en el cliente sobre la pagina actual. */
type Tarjeta = keyof ResumenClientesApi;

type Orden = 'cliente' | 'estado' | 'ultimoUso';

/** Fase 3: que archivo se baja desde el bloque «Descargas». */
type DescargaDoc = 'openapi' | 'postman' | 'qa' | 'prod' | 'entorno' | 'openapiOperadores' | 'postmanOperadores' | 'entornoOperadores';

/** Fase 3: las dos especificaciones del bloque Swagger. */
type PestanaSwagger = 'cliente' | 'operadores';

/** Lo que se edita en el formulario de alta y en el de configuracion. */
interface FormularioCliente {
  todos: boolean;
  productos: Set<string>;
  limitePorMinuto: number;
  /** `general` = no manda límite (alta) o manda `null` (configurar). */
  limiteModo: 'general' | 'personalizado';
  maxLlaves: number;
  nota: string;
}

/** Rango del límite personalizado (contrato del back). */
const LIMITE_MIN = 1;
const LIMITE_MAX = 600;

/**
 * Clientes API (panel de operadores).
 *
 * El operador invita a un cliente aprobado a usar la API de tasas, le fija
 * el limite, lo deshabilita con motivo, lo rehabilita y le reenvia el correo.
 * Desde la fase 2 el alta ya no pide productos (se manda `['*']`; el catalogo
 * y sus metodos quedan por compatibilidad) y el operador ve la llave del
 * cliente (kid, IPs, uso, vencimiento) y puede revocarla en emergencia con
 * un motivo que el cliente lee. No crea llaves ni ve claves: eso lo hace el
 * cliente desde su cuenta. Nada se borra.
 */
@Component({
  selector: 'app-clientes-api-admin',
  templateUrl: './clientes-api-admin.component.html',
  styleUrls: ['./clientes-api-admin.component.sass'],
})
export class ClientesApiAdminComponent implements OnInit, OnDestroy {
  readonly tamanosPagina = TAMANOS_PAGINA;
  porPagina = POR_PAGINA_DEFAULT;

  // ------------------------------------------------------------ lista
  items: ClienteApi[] = [];
  total = 0;
  page = 1;
  q = '';
  estado: EstadoClienteApi | '' = '';
  cargando = false;
  /** Recarga en segundo plano (refresco automatico): no esconde la tabla. */
  refrescando = false;
  error = '';
  aviso = '';
  ocupado = false;
  actualizadoEn: Date | null = null;

  // ------------------------------------------------------------ vista y documentacion
  /** «Clientes» (lista) o «Documentación»; el tablero de arriba se ve siempre. */
  vista: 'lista' | 'documentacion' = 'lista';
  documentacion: DocumentacionControlApi | null = null;
  cargandoDoc = false;
  errorDoc = '';
  descargandoOpenapi = false;
  errorOpenapi = '';

  // ------------------------------------------------------------ fase 3: swagger y descargas
  /** Bloque plegable «Especificación OpenAPI (Swagger)»: arranca plegado (patron del panel). */
  swaggerAbierto = false;
  swaggerPestana: PestanaSwagger = 'cliente';
  /** YAML ya bajado con el token, por especificacion. `null` = no cargado. */
  openapiYaml: Record<PestanaSwagger, string | null> = { cliente: null, operadores: null };
  errorSwaggerPor: Record<PestanaSwagger, string> = { cliente: '', operadores: '' };
  /** Especificacion que se esta bajando (`''` = ninguna). */
  cargandoSwaggerDe: PestanaSwagger | '' = '';
  descargando: DescargaDoc | '' = '';
  errorDescarga = '';

  // ------------------------------------------------------------ interruptor
  interruptor: InterruptorApi | null = null;
  cargandoInterruptor = false;
  errorInterruptor = '';

  // ------------------------------------------------------------ resumen y orden
  resumen: ResumenClientesApi | null = null;
  cargandoResumen = false;
  /** Tarjeta elegida; `null` = sin filtro de tarjeta. */
  tarjeta: Tarjeta | null = null;
  orden: Orden | null = null;
  ordenAsc = true;

  // ------------------------------------------------------------ uso en el detalle
  usoDetalle: FilaUsoClienteApi[] | null = null;
  cargandoUso = false;
  errorUso = '';

  private q$ = new Subject<string>();
  private temporizador: ReturnType<typeof setInterval> | null = null;
  private readonly alCambiarVisibilidad = (): void => {
    if (document.visibilityState === 'visible') void this.refrescar();
  };

  // ------------------------------------------------------------ catalogo
  productos: ProductoApi[] = [];

  // ------------------------------------------------------------ alta
  invitando = false;
  busqueda = '';
  buscando = false;
  candidatos: UsuarioCandidatoApi[] = [];
  busquedaHecha = false;
  elegido: UsuarioCandidatoApi | null = null;
  formAlta: FormularioCliente = this.formularioVacio();
  errorAlta = '';
  causasAlta: string[] = [];

  // ------------------------------------------------------------ configurar
  configurando: ClienteApi | null = null;
  formConfig: FormularioCliente = this.formularioVacio();
  errorConfig = '';

  // ------------------------------------------------------------ detalle
  detalleDe: number | null = null;
  detalle: ClienteApiDetalle | null = null;
  cargandoDetalle = false;

  private busqueda$ = new Subject<string>();
  private subs = new Subscription();

  constructor(private api: ApiExternaControlService) {}

  ngOnInit(): void {
    this.subs.add(
      this.busqueda$
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((q) => void this.buscar(q))
    );
    this.subs.add(
      this.q$.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => void this.cargar(1))
    );
    void this.cargar(1);
    void this.cargarResumen();
    void this.cargarInterruptor();
    // PX-01 (03/11): el panel ya no muestra productos; `cargarProductos` queda sin uso.
    this.temporizador = setInterval(() => void this.refrescar(), REFRESCO_MS);
    document.addEventListener('visibilitychange', this.alCambiarVisibilidad);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    if (this.temporizador) clearInterval(this.temporizador);
    document.removeEventListener('visibilitychange', this.alCambiarVisibilidad);
  }

  // ================================================================ lista

  /**
   * Carga una pagina con los filtros vigentes. `silencioso` (refresco
   * automatico) no esconde la tabla ni pisa un error visible.
   */
  async cargar(page: number, silencioso = false): Promise<void> {
    if (silencioso) this.refrescando = true;
    else {
      this.cargando = true;
      this.error = '';
    }
    try {
      const r = await this.api.listar({
        estado: this.estado,
        q: this.q,
        page,
        limit: this.porPagina,
      });
      this.items = r.items ?? [];
      this.total = r.total ?? 0;
      this.page = page;
      this.actualizadoEn = new Date();
      // Si la pagina quedo vacia porque la lista se achico, se vuelve a la ultima.
      if (!this.items.length && page > 1 && this.total > 0) {
        await this.cargar(this.totalPaginas(), silencioso);
      }
    } catch (e) {
      if (!silencioso) this.error = this.mensajeDe(e, 'No se pudo cargar la lista de clientes API.');
    } finally {
      this.cargando = false;
      this.refrescando = false;
    }
  }

  totalPaginas(): number {
    return Math.max(1, Math.ceil(this.total / this.porPagina));
  }

  /** Primer y ultimo numero de fila visibles («21–40 de 87»). */
  get rangoVisible(): string {
    if (!this.total) return '0';
    const desde = (this.page - 1) * this.porPagina + 1;
    const hasta = Math.min(this.total, desde + this.items.length - 1);
    return `${desde}–${hasta} de ${this.total}`;
  }

  cambiarTamano(valor: string | number): void {
    const n = Number(valor);
    if (!TAMANOS_PAGINA.includes(n) || n === this.porPagina) return;
    this.porPagina = n;
    void this.cargar(1);
  }

  cambioQ(valor: string): void {
    this.q = valor;
    this.q$.next(valor.trim());
  }

  /** Boton «Actualizar»: lista y resumen, manteniendo pagina y filtros. */
  async actualizar(): Promise<void> {
    await Promise.all([this.cargar(this.page), this.cargarResumen(), this.cargarInterruptor()]);
  }

  /**
   * Refresco automatico: solo con la pestana visible y sin dialogos abiertos
   * ni acciones en curso (no pisar lo que el operador esta tocando).
   */
  private async refrescar(): Promise<void> {
    if (document.visibilityState !== 'visible') return;
    if (this.ocupado || this.cargando || this.refrescando || this.invitando || this.configurando) return;
    await Promise.all([
      this.cargar(this.page, true),
      this.cargarResumen(true),
      this.cargarInterruptor(true),
    ]);
  }

  // ================================================================ documentacion

  cambiarVista(v: 'lista' | 'documentacion'): void {
    this.vista = v;
    if (v === 'documentacion' && !this.documentacion && !this.cargandoDoc) {
      void this.cargarDocumentacion();
    }
  }

  async cargarDocumentacion(): Promise<void> {
    this.cargandoDoc = true;
    this.errorDoc = '';
    try {
      this.documentacion = await this.api.documentacion();
    } catch (e) {
      const status = (e as { status?: number })?.status;
      this.errorDoc =
        status === 404
          ? 'El backend todavía no expone la documentación para operadores.'
          : this.mensajeDe(e, 'No se pudo cargar la documentación.');
    } finally {
      this.cargandoDoc = false;
    }
  }

  /** Baja el OpenAPI con el token de siempre (lo pone el interceptor) y lo guarda como archivo. */
  async descargarOpenapi(): Promise<void> {
    if (this.descargandoOpenapi) return;
    this.descargandoOpenapi = true;
    this.errorOpenapi = '';
    try {
      const blob = await this.api.openapi();
      saveAs(blob, NOMBRE_OPENAPI);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      this.errorOpenapi =
        status === 404
          ? 'El archivo OpenAPI todavía no está disponible.'
          : this.mensajeDe(e, 'No se pudo descargar el OpenAPI.');
    } finally {
      this.descargandoOpenapi = false;
    }
  }

  // ================================================================ fase 3: swagger y descargas

  alternarSwagger(): void {
    this.swaggerAbierto = !this.swaggerAbierto;
    if (this.swaggerAbierto) void this.cargarSwagger();
  }

  cambiarSwaggerPestana(p: PestanaSwagger): void {
    this.swaggerPestana = p;
    void this.cargarSwagger();
  }

  get swaggerYaml(): string | null {
    return this.openapiYaml[this.swaggerPestana];
  }

  get swaggerError(): string {
    return this.errorSwaggerPor[this.swaggerPestana];
  }

  get swaggerCargando(): boolean {
    return this.cargandoSwaggerDe === this.swaggerPestana;
  }

  /** Baja el YAML de la pestana activa si todavia no esta (o si se pide `forzar`). */
  async cargarSwagger(forzar = false): Promise<void> {
    const pestana = this.swaggerPestana;
    if (this.cargandoSwaggerDe) return;
    if (!forzar && this.openapiYaml[pestana] !== null) return;
    this.cargandoSwaggerDe = pestana;
    this.errorSwaggerPor[pestana] = '';
    try {
      this.openapiYaml[pestana] =
        pestana === 'cliente' ? await this.api.openapiTexto() : await this.api.openapiOperadoresTexto();
    } catch (e) {
      this.openapiYaml[pestana] = null;
      const status = (e as { status?: number })?.status;
      this.errorSwaggerPor[pestana] =
        status === 404
          ? pestana === 'cliente'
            ? 'El backend todavía no publica el OpenAPI del cliente.'
            : 'El backend todavía no publica el OpenAPI de operadores.'
          : this.mensajeDe(e, 'No se pudo cargar la especificación.');
    } finally {
      this.cargandoSwaggerDe = '';
    }
  }

  /**
   * Bloque «Descargas»: todo lo que sirve el modulo (cliente y operadores).
   * El nombre del archivo lo manda el servidor en `Content-Disposition`.
   */
  async descargar(que: DescargaDoc): Promise<void> {
    if (this.descargando) return;
    this.descargando = que;
    this.errorDescarga = '';
    try {
      const pedidos: Record<DescargaDoc, () => Promise<ArchivoDescargado>> = {
        openapi: () => this.api.openapiArchivo(),
        postman: () => this.api.postman(),
        qa: () => this.api.postmanEntorno('qa'),
        prod: () => this.api.postmanEntorno('prod'),
        entorno: () => this.api.postmanEntornoActual(),
        openapiOperadores: () => this.api.openapiOperadores(),
        postmanOperadores: () => this.api.postmanOperadores(),
        entornoOperadores: () => this.api.postmanEntornoOperadores(),
      };
      const archivo = await pedidos[que]();
      saveAs(archivo.blob, archivo.nombre);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      this.errorDescarga =
        status === 404
          ? 'Ese archivo todavía no está publicado en el backend.'
          : this.mensajeDe(e, 'No se pudo descargar el archivo.');
    } finally {
      this.descargando = '';
    }
  }

  // ================================================================ sesion (fase 3)

  /** Sesiones vivas del cliente; `null` si el backend todavia no lo manda. */
  sesionesDe(c: ClienteApi): number | null {
    const n = c.sesionesActivas ?? c.conexion?.sesionesActivas;
    if (typeof n === 'number' && Number.isFinite(n)) return n;
    if (c.sesionActiva === true) return 1;
    if (c.sesionActiva === false) return 0;
    return null;
  }

  ultimaSesionDe(c: ClienteApi): string | null {
    return c.ultimaSesionEn ?? c.conexion?.ultimaSesionEn ?? null;
  }

  textoSesion(c: ClienteApi): string {
    const n = this.sesionesDe(c);
    if (n === null) return '—';
    if (n <= 0) return 'Sin sesión';
    return n === 1 ? '1 activa' : `${n} activas`;
  }

  claseSesion(c: ClienteApi): string {
    const n = this.sesionesDe(c);
    if (n === null) return 'sin-dato';
    return n > 0 ? 'ok' : 'espera';
  }

  tooltipSesion(c: ClienteApi): string {
    const n = this.sesionesDe(c);
    if (n === null) return 'El backend todavía no informa las sesiones.';
    const f = this.ultimaSesionDe(c);
    const partes = [n > 0 ? `${n} sesión(es) viva(s) de 15 min atadas a IP` : 'Sin sesión viva (puede seguir firmando por petición)'];
    if (f) partes.push(`Última sesión: ${this.fechaCorta(f)}`);
    return partes.join('\n');
  }

  // ================================================================ interruptor

  async cargarInterruptor(silencioso = false): Promise<void> {
    if (!silencioso) this.cargandoInterruptor = true;
    try {
      this.interruptor = await this.api.interruptor();
    } catch {
      // Backend sin `/interruptor` todavia: el bloque muestra «—».
      if (!silencioso) this.interruptor = null;
    } finally {
      this.cargandoInterruptor = false;
    }
  }

  /** Encender/apagar la API para todos los clientes, con motivo obligatorio. */
  async alternarInterruptor(): Promise<void> {
    const actual = this.interruptor;
    if (!actual || this.ocupado) return;
    const encender = !actual.activa;
    const r = await Swal.fire({
      icon: encender ? 'question' : 'warning',
      title: encender ? 'Encender la API externa' : 'Apagar la API externa',
      html: encender
        ? 'Los clientes con llave activa vuelven a poder consultar tasas y saldos en menos de un minuto.'
        : '<b>Todos</b> los clientes reciben <code>503</code> en menos de un minuto, sin importar su llave. ' +
          'Las llaves no se tocan: al encender vuelven a servir.',
      input: 'text',
      inputLabel: 'Motivo (obligatorio, queda en la bitácora)',
      inputPlaceholder: encender ? 'Por qué se enciende' : 'Por qué se apaga',
      inputAttributes: { maxlength: '255' },
      inputValidator: (v) => (v && v.trim().length >= 3 ? null : 'Escribí el motivo (al menos 3 caracteres).'),
      showCancelButton: true,
      confirmButtonText: encender ? 'Encender' : 'Apagar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: encender ? VERDE : ROJO,
    });
    if (!r.isConfirmed) return;
    this.ocupado = true;
    this.errorInterruptor = '';
    this.aviso = '';
    try {
      const nuevo = await this.api.cambiarInterruptor(encender, String(r.value));
      this.interruptor = nuevo && typeof nuevo.activa === 'boolean' ? nuevo : { ...actual, activa: encender };
      this.aviso = encender ? 'API externa encendida.' : 'API externa apagada: los clientes reciben 503.';
      void this.cargarInterruptor(true);
    } catch (e) {
      const { mensaje, causas } = this.errorConCausas(e, 'No se pudo cambiar el interruptor.');
      this.errorInterruptor = causas.length ? `${mensaje} ${causas.join(' · ')}` : mensaje;
    } finally {
      this.ocupado = false;
    }
  }

  // ================================================================ resumen

  async cargarResumen(silencioso = false): Promise<void> {
    if (!silencioso) this.cargandoResumen = true;
    try {
      this.resumen = await this.api.resumen();
    } catch {
      // Backend sin `/resumen` todavia: las tarjetas muestran «—».
      if (!silencioso) this.resumen = null;
    } finally {
      this.cargandoResumen = false;
    }
  }

  valorTarjeta(t: Tarjeta): number | null {
    const v = this.resumen?.[t];
    return typeof v === 'number' ? v : null;
  }

  /**
   * Clic en una tarjeta. Las de estado filtran en el servidor (`estado`); las
   * de llave y conexion filtran en el cliente sobre la pagina actual porque
   * el listado del contrato no tiene ese parametro. Clic de nuevo: quita.
   */
  elegirTarjeta(t: Tarjeta): void {
    if (this.tarjeta === t) {
      this.tarjeta = null;
      this.estado = '';
      void this.cargar(1);
      return;
    }
    this.tarjeta = t;
    const porEstado: Partial<Record<Tarjeta, EstadoClienteApi | ''>> = {
      habilitados: '',
      activos: 'ACTIVO',
      invitados: 'INVITADO',
      deshabilitados: 'DESHABILITADO',
    };
    if (t in porEstado) {
      this.estado = porEstado[t] ?? '';
      void this.cargar(1);
    } else if (this.estado === 'DESHABILITADO') {
      // Con llave / conectados / con errores no tienen sentido entre deshabilitados.
      this.estado = '';
      void this.cargar(1);
    }
  }

  /** La pagina actual con el filtro de tarjeta (cliente) y el orden elegido. */
  get itemsVista(): ClienteApi[] {
    let lista = this.items;
    switch (this.tarjeta) {
      case 'habilitados':
        lista = lista.filter((c) => c.estado !== 'DESHABILITADO');
        break;
      case 'conLlave':
        lista = lista.filter((c) => c.llave?.estado === 'ACTIVA');
        break;
      case 'conectadosHoy':
        lista = lista.filter((c) => (c.conexion?.llamadasHoy ?? 0) > 0);
        break;
      case 'conErroresHoy':
        lista = lista.filter((c) => (c.conexion?.rechazadasHoy ?? 0) > 0 || c.conexion?.estado === 'CON_ERRORES');
        break;
      case 'conSesionActiva':
        lista = lista.filter((c) => (this.sesionesDe(c) ?? 0) > 0);
        break;
    }
    if (!this.orden) return lista;
    const dir = this.ordenAsc ? 1 : -1;
    const pesoEstado: Record<string, number> = { ACTIVO: 0, INVITADO: 1, DESHABILITADO: 2 };
    return [...lista].sort((a, b) => {
      switch (this.orden) {
        case 'cliente':
          return dir * this.nombreDe(a).localeCompare(this.nombreDe(b), 'es', { sensitivity: 'base' });
        case 'estado':
          return dir * ((pesoEstado[a.estado] ?? 9) - (pesoEstado[b.estado] ?? 9));
        case 'ultimoUso': {
          const ta = this.msUltimoUso(a);
          const tb = this.msUltimoUso(b);
          return dir * (ta - tb);
        }
        default:
          return 0;
      }
    });
  }

  /** El filtro de tarjeta en el cliente solo ve la pagina actual: se avisa. */
  get filtroEnCliente(): boolean {
    if (this.tarjeta === 'conSesionActiva') return true;
    return this.tarjeta === 'conLlave' || this.tarjeta === 'conectadosHoy' || this.tarjeta === 'conErroresHoy';
  }

  ordenarPor(o: Orden): void {
    if (this.orden === o) {
      if (!this.ordenAsc) {
        this.orden = null;
        this.ordenAsc = true;
      } else {
        this.ordenAsc = false;
      }
    } else {
      this.orden = o;
      this.ordenAsc = true;
    }
  }

  flechaOrden(o: Orden): string {
    if (this.orden !== o) return '';
    return this.ordenAsc ? '▲' : '▼';
  }

  private msUltimoUso(c: ClienteApi): number {
    const f = c.conexion?.ultimoOkEn ?? c.llave?.ultimoUsoEn ?? null;
    const ms = f ? new Date(f).getTime() : NaN;
    return Number.isFinite(ms) ? ms : -1;
  }

  // ================================================================ conexion

  claseConexion(c: ClienteApi): string {
    const e = c.conexion?.estado;
    if (e === 'OK') return 'ok';
    if (e === 'CON_ERRORES') return 'mal';
    if (e === 'NUNCA' || e === 'SIN_LLAVE' || e === 'INACTIVA') return 'espera';
    return 'sin-dato';
  }

  textoConexion(c: ClienteApi): string {
    const x = c.conexion;
    if (!x) return '—';
    switch (x.estado) {
      case 'OK':
        return x.ultimoOkEn ? `OK · ${this.haceCuanto(x.ultimoOkEn)}` : 'OK';
      case 'CON_ERRORES':
        return x.ultimoFalloCodigo ? `Errores · ${x.ultimoFalloCodigo}` : 'Con errores';
      case 'SIN_LLAVE':
        return 'Sin llave';
      case 'NUNCA':
        return 'Nunca se conectó';
      case 'INACTIVA':
        return x.ultimoOkEn ? `Inactiva · ${this.haceCuanto(x.ultimoOkEn)}` : 'Inactiva';
      default:
        return String(x.estado);
    }
  }

  tooltipConexion(c: ClienteApi): string {
    const x = c.conexion;
    if (!x) return 'El backend todavía no informa la conexión.';
    const partes: string[] = [];
    if (x.ultimoOkEn) partes.push(`Último OK: ${this.fechaCorta(x.ultimoOkEn)}`);
    if (x.ultimoFalloEn) {
      partes.push(
        `Último fallo: ${this.fechaCorta(x.ultimoFalloEn)}` +
          (x.ultimoFalloCodigo ? ` · ${x.ultimoFalloCodigo}` : '') +
          (x.ultimoFalloIp ? ` · IP ${x.ultimoFalloIp}` : '')
      );
    }
    if (!partes.length) partes.push(x.estado === 'SIN_LLAVE' ? 'Todavía no creó su llave.' : 'Sin llamadas registradas.');
    return partes.join('\n');
  }

  hoyConexion(c: ClienteApi): string {
    const x = c.conexion;
    if (!x) return '—';
    return `hoy: ${x.llamadasHoy ?? 0} ok / ${x.rechazadasHoy ?? 0} rechazadas`;
  }

  /** «hace 3 min», «hace 2 h», «hace 5 d». */
  haceCuanto(fecha: string | Date | null | undefined): string {
    if (!fecha) return '—';
    const ms = Date.now() - new Date(fecha).getTime();
    if (!Number.isFinite(ms) || ms < 0) return 'ahora';
    const min = Math.floor(ms / 60_000);
    if (min < 1) return 'hace instantes';
    if (min < 60) return `hace ${min} min`;
    const h = Math.floor(min / 60);
    if (h < 48) return `hace ${h} h`;
    return `hace ${Math.floor(h / 24)} d`;
  }

  private fechaCorta(f: string): string {
    const d = new Date(f);
    if (!Number.isFinite(d.getTime())) return f;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm} ${hh}:${mi}`;
  }

  async cargarUsoDetalle(c: ClienteApi): Promise<void> {
    this.cargandoUso = true;
    this.errorUso = '';
    try {
      this.usoDetalle = await this.api.usoCliente(c.usuarioId, 7);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      this.errorUso =
        status === 404
          ? 'El backend todavía no expone el uso por cliente.'
          : this.mensajeDe(e, 'No se pudo cargar el uso de la llave.');
    } finally {
      this.cargandoUso = false;
    }
  }

  totalUso(campo: 'ok' | 'http401' | 'http403' | 'http429'): number {
    return (this.usoDetalle ?? []).reduce((s, f) => s + (Number(f[campo]) || 0), 0);
  }

  private async cargarProductos(): Promise<void> {
    try {
      this.productos = await this.api.productos();
    } catch {
      this.productos = [];
    }
  }

  // ================================================================ vista

  nombreDe(c: { nombre?: string; apellidos?: string }): string {
    return `${c.nombre ?? ''} ${c.apellidos ?? ''}`.trim() || '—';
  }

  etiquetaEstado(estado: string): string {
    switch (estado) {
      case 'INVITADO':
        return 'Invitado';
      case 'ACTIVO':
        return 'Activo';
      case 'DESHABILITADO':
        return 'Deshabilitado';
      default:
        return estado;
    }
  }

  /** `CLP_VES` → `CLP → VES`; `*` → «Todos». */
  nombreProducto(id: string): string {
    if (id === '*') return 'Todos';
    const p = this.productos.find((x) => x.id === id);
    if (p) return `${p.origen} → ${p.destino}`;
    const partes = id.split('_');
    return partes.length === 2 ? `${partes[0]} → ${partes[1]}` : id;
  }

  productosDe(c: ClienteApi): string {
    const lista = c.productos ?? [];
    if (!lista.length) return '—';
    if (lista.includes('*')) return 'Todos';
    return lista.map((id) => this.nombreProducto(id)).join(', ');
  }

  limiteDe(c: ClienteApi): string {
    const origen = this.origenLimite(c);
    const valor = `${this.limiteEfectivo(c)}/min`;
    if (origen === 'general') return `General · ${valor}`;
    if (origen === 'personalizado') return `Personalizado · ${valor}`;
    return valor;
  }

  /** Valor efectivo: `limite.porMinuto` del back nuevo, o el campo viejo. */
  limiteEfectivo(c: ClienteApi): number {
    return c.limite?.porMinuto ?? c.limitePorMinuto ?? this.limiteGeneral ?? LIMITE_DEFAULT;
  }

  /** `null` si el back todavía no manda `limite` (se muestra solo el número). */
  origenLimite(c: ClienteApi): 'general' | 'personalizado' | null {
    return c.limite?.origen ?? null;
  }

  /** Límite general vigente, tomado de cualquier fila que lo traiga. */
  get limiteGeneral(): number | null {
    const desde = [this.detalle, this.configurando, ...this.items];
    for (const c of desde) {
      const g = c?.limite?.general;
      if (typeof g === 'number' && g > 0) return g;
    }
    return null;
  }

  get etiquetaLimiteGeneral(): string {
    const g = this.limiteGeneral;
    return g ? `Usar el límite general (${g}/min)` : 'Usar el límite general';
  }

  /** Lo que se manda: `null` = general, número = personalizado. */
  private limiteDelFormulario(f: FormularioCliente): number | null {
    return f.limiteModo === 'personalizado' ? f.limitePorMinuto : null;
  }

  private textoLimiteFormulario(f: FormularioCliente): string {
    const n = this.limiteDelFormulario(f);
    return n === null ? `general${this.limiteGeneral ? ` (${this.limiteGeneral}/min)` : ''}` : `${n}/min personalizado`;
  }

  verificacionDe(c: ClienteApi): string {
    if (!c.ultimaVerificacionEn) return 'Sin verificar';
    return c.ultimaVerificacionResultado || 'OK';
  }

  etiquetaEvento(tipo: string): string {
    const nombres: Record<string, string> = {
      INVITAR: 'Invitado',
      ACTIVAR: 'Activó su cuenta',
      CONFIGURAR: 'Configuración',
      DESHABILITAR: 'Deshabilitado',
      REHABILITAR: 'Rehabilitado',
      REENVIAR_CORREO: 'Correo reenviado',
      VIGILANTE_DESHABILITAR: 'Deshabilitado por el vigilante',
      CREAR_LLAVE: 'Llave creada',
      ROTAR_LLAVE: 'Llave rotada',
      REVOCAR_LLAVE: 'Llave revocada',
      CAMBIAR_IPS: 'IPs cambiadas',
    };
    return nombres[tipo] ?? tipo;
  }

  detalleEvento(detalle: unknown): string {
    if (detalle === null || detalle === undefined || detalle === '') return '';
    if (typeof detalle === 'string') return detalle;
    try {
      return JSON.stringify(detalle);
    } catch {
      return '';
    }
  }

  // ================================================================ llave (fase 2)

  etiquetaLlave(l: LlaveClienteApi | null | undefined): string {
    if (!l) return 'Sin llave';
    if (l.estado === 'REVOCADA') return 'Revocada';
    if (l.expiraEn && new Date(l.expiraEn).getTime() < Date.now()) return 'Vencida';
    return 'Activa';
  }

  /** Clase del chip: la vencida se pinta como revocada (ya no sirve para firmar). */
  claseLlave(l: LlaveClienteApi | null | undefined): string {
    const e = this.etiquetaLlave(l);
    return e === 'Activa' ? 'ACTIVA' : e === 'Sin llave' ? '' : 'REVOCADA';
  }

  ipsDe(l: LlaveClienteApi | null | undefined): string {
    return l?.ips?.length ? l.ips.join(', ') : '—';
  }

  puedeRevocarLlave(c: ClienteApi): boolean {
    return c.llave?.estado === 'ACTIVA';
  }

  /** `POST llaves/:id/revocar`: el id es el de la fila; si no viniera, se cae al kid. */
  private idLlave(l: LlaveClienteApi): string {
    return l.id || l.kid;
  }

  async revocarLlave(c: ClienteApi): Promise<void> {
    const l = c.llave;
    if (!l || l.estado !== 'ACTIVA') return;
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Revocar la llave del cliente',
      html:
        `<b>${this.escapar(this.nombreDe(c))}</b><br>${this.escapar(c.email)}<br>` +
        `<code>${this.escapar(l.kid)}</code><br><br>` +
        'Su sistema deja de poder firmar peticiones en menos de un minuto. ' +
        'El cliente puede crear una llave nueva desde su cuenta. Nada se borra.<br><br>' +
        '<b>El motivo que escribas lo ve el cliente</b> en su sección API y en el correo que le llega.',
      input: 'text',
      inputLabel: 'Motivo (obligatorio, lo lee el cliente)',
      inputPlaceholder: 'Por qué se revoca',
      inputAttributes: { maxlength: '255' },
      inputValidator: (v) => (v && v.trim().length >= 3 ? null : 'Escribí el motivo (al menos 3 caracteres).'),
      showCancelButton: true,
      confirmButtonText: 'Revocar llave',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: ROJO,
    });
    if (!r.isConfirmed) return;
    this.ocupado = true;
    this.error = '';
    this.aviso = '';
    try {
      await this.api.revocarLlave(this.idLlave(l), String(r.value).trim());
      this.aviso = `Llave ${l.kid} de ${this.nombreDe(c)} revocada.`;
      // La respuesta puede ser la llave o el cliente: se recarga la lista (misma
      // pagina y filtros), el resumen y el detalle abierto para no adivinar la forma.
      await Promise.all([this.cargar(this.page), this.cargarResumen(true)]);
      if (this.detalleDe === c.usuarioId) {
        try {
          this.detalle = await this.api.detalle(c.usuarioId);
        } catch {
          // El detalle viejo queda; la lista ya esta fresca.
        }
      }
    } catch (e) {
      const { mensaje, causas } = this.errorConCausas(e, 'No se pudo revocar la llave.');
      this.error = causas.length ? `${mensaje} ${causas.join(' · ')}` : mensaje;
    } finally {
      this.ocupado = false;
    }
  }

  // ================================================================ alta

  abrirInvitar(): void {
    this.invitando = true;
    this.busqueda = '';
    this.candidatos = [];
    this.busquedaHecha = false;
    this.buscando = false;
    this.elegido = null;
    this.formAlta = this.formularioVacio();
    this.errorAlta = '';
    this.causasAlta = [];
  }

  cerrarInvitar(): void {
    if (this.ocupado) return;
    this.invitando = false;
  }

  cambioBusqueda(valor: string): void {
    this.busqueda = valor;
    const q = valor.trim();
    if (q.length < 3) {
      this.candidatos = [];
      this.busquedaHecha = false;
      this.buscando = false;
      return;
    }
    this.buscando = true;
    this.busqueda$.next(q);
  }

  private async buscar(q: string): Promise<void> {
    if (q.length < 3) return;
    try {
      const r = await this.api.buscarUsuarios(q);
      // Si el operador siguio escribiendo, esta respuesta ya es vieja.
      if (this.busqueda.trim() !== q) return;
      this.candidatos = r ?? [];
      this.busquedaHecha = true;
    } catch (e) {
      if (this.busqueda.trim() !== q) return;
      this.candidatos = [];
      this.busquedaHecha = true;
      this.errorAlta = this.mensajeDe(e, 'No se pudo buscar. Probá de nuevo.');
    } finally {
      if (this.busqueda.trim() === q) this.buscando = false;
    }
  }

  elegir(u: UsuarioCandidatoApi): void {
    if (!u.elegible) return;
    this.elegido = u;
    this.formAlta = this.formularioVacio();
    this.errorAlta = '';
    this.causasAlta = [];
  }

  volverABuscar(): void {
    this.elegido = null;
    this.errorAlta = '';
    this.causasAlta = [];
  }

  alternarTodos(f: FormularioCliente, todos: boolean): void {
    f.todos = todos;
    if (todos) f.productos.clear();
  }

  alternarProducto(f: FormularioCliente, id: string, marcado: boolean): void {
    if (marcado) {
      f.productos.add(id);
      f.todos = false;
    } else {
      f.productos.delete(id);
    }
  }

  formularioValido(f: FormularioCliente): boolean {
    const hayProductos = f.todos || f.productos.size > 0;
    const limiteOk =
      f.limiteModo === 'general' ||
      (Number.isInteger(f.limitePorMinuto) && f.limitePorMinuto >= LIMITE_MIN && f.limitePorMinuto <= LIMITE_MAX);
    const llavesOk = Number.isInteger(f.maxLlaves) && f.maxLlaves >= 1;
    return hayProductos && limiteOk && llavesOk;
  }

  async confirmarAlta(): Promise<void> {
    if (!this.elegido || !this.formularioValido(this.formAlta)) return;
    const u = this.elegido;
    const limite = this.limiteDelFormulario(this.formAlta);
    const ok = await Swal.fire({
      icon: 'question',
      title: 'Invitar a usar la API',
      html:
        `<b>${this.escapar(this.nombreDe(u))}</b><br>${this.escapar(u.email)}<br><br>` +
        `Límite: ${this.textoLimiteFormulario(this.formAlta)} · Llaves: ${this.formAlta.maxLlaves}<br><br>` +
        'Le va a llegar el correo de habilitación.',
      showCancelButton: true,
      confirmButtonText: 'Invitar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: VERDE,
    });
    if (!ok.isConfirmed) return;

    this.ocupado = true;
    this.errorAlta = '';
    this.causasAlta = [];
    try {
      const creado = await this.api.crear({
        usuarioId: u.usuarioId,
        // PX-01: sin `productos`. Límite general = no se manda.
        ...(limite !== null ? { limitePorMinuto: limite } : {}),
        maxLlaves: this.formAlta.maxLlaves,
        nota: this.formAlta.nota.trim() || undefined,
      });
      this.invitando = false;
      this.aviso =
        creado?.correoEnviado === false
          ? `${this.nombreDe(u)} quedó invitado, pero el correo no salió. Usá «Reenviar correo».`
          : `${this.nombreDe(u)} quedó invitado. Le enviamos el correo a ${u.email}.`;
      this.error = '';
      await Promise.all([this.cargar(1), this.cargarResumen(true)]);
    } catch (e) {
      const { mensaje, causas } = this.errorConCausas(e, 'No se pudo invitar al cliente.');
      this.errorAlta = mensaje;
      this.causasAlta = causas;
    } finally {
      this.ocupado = false;
    }
  }

  // ================================================================ configurar

  abrirConfigurar(c: ClienteApi): void {
    this.configurando = c;
    this.errorConfig = '';
    // Fase 2: los productos ya no se eligen por cliente; se manda siempre `['*']`.
    this.formConfig = {
      todos: true,
      productos: new Set<string>(),
      limitePorMinuto: this.limiteEfectivo(c),
      // Back nuevo: `limite.origen`. Back viejo: `limitePorMinuto` propio = personalizado.
      limiteModo:
        (c.limite?.origen ?? (c.limitePorMinuto != null ? 'personalizado' : 'general')) === 'personalizado'
          ? 'personalizado'
          : 'general',
      maxLlaves: c.maxLlaves ?? MAX_LLAVES_DEFAULT,
      nota: c.nota ?? '',
    };
  }

  cerrarConfigurar(): void {
    if (this.ocupado) return;
    this.configurando = null;
  }

  async guardarConfiguracion(): Promise<void> {
    const c = this.configurando;
    if (!c || !this.formularioValido(this.formConfig)) return;
    this.ocupado = true;
    this.errorConfig = '';
    try {
      // Fase 2: el PUT ya no manda `productos` (el campo del tipo queda opcional).
      const actualizado = await this.api.configurar(c.usuarioId, {
        // `null` = vuelve al límite general.
        limitePorMinuto: this.limiteDelFormulario(this.formConfig),
        maxLlaves: this.formConfig.maxLlaves,
        nota: this.formConfig.nota.trim(),
      });
      this.reemplazar(actualizado);
      this.configurando = null;
      this.aviso = `Configuración de ${this.nombreDe(actualizado)} guardada.`;
      this.error = '';
    } catch (e) {
      this.errorConfig = this.mensajeDe(e, 'No se pudo guardar la configuración.');
    } finally {
      this.ocupado = false;
    }
  }

  // ================================================================ acciones

  async deshabilitar(c: ClienteApi): Promise<void> {
    const r = await Swal.fire({
      icon: 'warning',
      title: 'Deshabilitar acceso a la API',
      html:
        `<b>${this.escapar(this.nombreDe(c))}</b><br>${this.escapar(c.email)}<br><br>` +
        'Deja de poder usar la API en menos de un minuto. Nada se borra.<br><br>' +
        '<b>El motivo que escribas lo ve el cliente</b> en su sección API y en el correo que le llega.',
      input: 'text',
      inputLabel: 'Motivo (obligatorio, lo lee el cliente)',
      inputPlaceholder: 'Por qué se deshabilita',
      inputAttributes: { maxlength: '255' },
      inputValidator: (v) => (v && v.trim().length >= 3 ? null : 'Escribí el motivo (al menos 3 caracteres).'),
      showCancelButton: true,
      confirmButtonText: 'Deshabilitar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: ROJO,
    });
    if (!r.isConfirmed) return;
    await this.accion(async () => {
      const actualizado = await this.api.deshabilitar(c.usuarioId, String(r.value).trim());
      this.reemplazar(actualizado);
      this.aviso = `${this.nombreDe(actualizado)} quedó deshabilitado.`;
    }, 'No se pudo deshabilitar.');
  }

  async rehabilitar(c: ClienteApi): Promise<void> {
    const r = await Swal.fire({
      icon: 'question',
      title: 'Rehabilitar acceso a la API',
      html:
        `<b>${this.escapar(this.nombreDe(c))}</b><br>${this.escapar(c.email)}<br><br>` +
        'Vuelve a INVITADO y se le reenvía el correo de habilitación. Solo si sigue 100 % activo.<br><br>' +
        '<b>Su llave vigente queda revocada:</b> el cliente tiene que crear una nueva desde su cuenta.',
      input: 'text',
      inputLabel: 'Nota (opcional)',
      inputAttributes: { maxlength: '255' },
      showCancelButton: true,
      confirmButtonText: 'Rehabilitar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: VERDE,
    });
    if (!r.isConfirmed) return;
    this.ocupado = true;
    this.error = '';
    this.aviso = '';
    try {
      const actualizado = await this.api.rehabilitar(c.usuarioId, r.value ? String(r.value) : undefined);
      this.reemplazar(actualizado);
      this.aviso = `${this.nombreDe(actualizado)} quedó rehabilitado. Le reenviamos el correo.`;
      void this.cargarResumen(true);
    } catch (e) {
      const { mensaje, causas } = this.errorConCausas(e, 'No se pudo rehabilitar.');
      this.error = causas.length ? `${mensaje} ${causas.join(' · ')}` : mensaje;
    } finally {
      this.ocupado = false;
    }
  }

  async reenviarCorreo(c: ClienteApi): Promise<void> {
    const ok = await Swal.fire({
      icon: 'question',
      title: 'Reenviar correo de habilitación',
      html: `Se le vuelve a enviar a <b>${this.escapar(c.email)}</b>.`,
      showCancelButton: true,
      confirmButtonText: 'Reenviar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: VERDE,
    });
    if (!ok.isConfirmed) return;
    await this.accion(async () => {
      await this.api.reenviarCorreo(c.usuarioId);
      this.aviso = `Correo reenviado a ${c.email}.`;
    }, 'No se pudo reenviar el correo.');
  }

  // ================================================================ detalle

  async alternarDetalle(c: ClienteApi): Promise<void> {
    if (this.detalleDe === c.usuarioId) {
      this.detalleDe = null;
      this.detalle = null;
      return;
    }
    this.detalleDe = c.usuarioId;
    this.detalle = null;
    this.usoDetalle = null;
    this.errorUso = '';
    this.cargandoDetalle = true;
    try {
      const d = await this.api.detalle(c.usuarioId);
      if (this.detalleDe === c.usuarioId) {
        this.detalle = d;
        // Si el detalle ya trae el uso, no hace falta pedirlo aparte.
        if (Array.isArray(d.uso)) this.usoDetalle = d.uso;
      }
    } catch (e) {
      if (this.detalleDe === c.usuarioId) {
        this.error = this.mensajeDe(e, 'No se pudo cargar el detalle.');
      }
    } finally {
      if (this.detalleDe === c.usuarioId) this.cargandoDetalle = false;
    }
  }

  // ================================================================ internos

  private productosDelFormulario(f: FormularioCliente): string[] {
    return f.todos ? ['*'] : Array.from(f.productos);
  }

  private formularioVacio(): FormularioCliente {
    return {
      // Fase 2: sin seleccion de productos en la vista; siempre «todos».
      todos: true,
      productos: new Set<string>(),
      limitePorMinuto: this.limiteGeneral ?? LIMITE_DEFAULT,
      limiteModo: 'general',
      maxLlaves: MAX_LLAVES_DEFAULT,
      nota: '',
    };
  }

  private reemplazar(actualizado: ClienteApi): void {
    const i = this.items.findIndex((x) => x.usuarioId === actualizado.usuarioId);
    if (i >= 0) this.items[i] = actualizado;
    if (this.detalle && this.detalle.usuarioId === actualizado.usuarioId) {
      this.detalle = { ...this.detalle, ...actualizado };
    }
  }

  private async accion(fn: () => Promise<void>, mensajeError: string): Promise<void> {
    this.ocupado = true;
    this.error = '';
    this.aviso = '';
    try {
      await fn();
      void this.cargarResumen(true);
    } catch (e) {
      this.error = this.mensajeDe(e, mensajeError);
    } finally {
      this.ocupado = false;
    }
  }

  /**
   * El interceptor de errores aplana casi todo a un string; las llamadas que
   * lo saltan traen el `HttpErrorResponse` entero. Se aceptan las dos formas.
   */
  private mensajeDe(e: unknown, porDefecto: string): string {
    if (typeof e === 'string' && e.trim()) return e;
    const cuerpo = (e as { error?: { message?: unknown } })?.error;
    const m = cuerpo?.message;
    if (Array.isArray(m) && m.length) return m.join(' · ');
    if (typeof m === 'string' && m.trim()) return m;
    return porDefecto;
  }

  private errorConCausas(e: unknown, porDefecto: string): { mensaje: string; causas: string[] } {
    const cuerpo = (e as { error?: { causas?: unknown } })?.error;
    const causas = Array.isArray(cuerpo?.causas)
      ? (cuerpo!.causas as unknown[]).map((c) => String(c))
      : [];
    return { mensaje: this.mensajeDe(e, porDefecto), causas };
  }

  private escapar(s: string): string {
    return String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
